import { useToggleBoolean } from '@bridebook/toolbox/src/utils/hooks/use-toggle-boolean';
import { useIsRsvpDeadlineValid } from '@bridebook/web/lib/wedding-website/hooks/use-is-rsvp-deadline-valid';
import { createContext, useCallback, useContext, useMemo } from 'react';
import { CreateMessageContainer } from './create-message-flow/create-message-container';
import { DirtyMessageFlowProvider } from '@bridebook/web/lib/inbox/guests/create-message-flow/dirty-message-flow-context';

type MessageGuestsModalContextType = {
  isModalOpen: boolean;
  openModal: () => void;
  closeModal: () => void;
  isDisabled: boolean;
};

const MessageGuestsModalContext = createContext<MessageGuestsModalContextType | undefined>(
  undefined,
);

export const useMessageGuestsModal = (): MessageGuestsModalContextType => {
  const context = useContext(MessageGuestsModalContext);
  if (!context) {
    throw new Error('useMessageGuestsModal must be used within a MessageGuestsModalProvider');
  }
  return context;
};

const MessageGuestsModalProvider = ({ children }: { children: React.ReactNode }) => {
  const [isModalOpen, openModalRaw, closeModal] = useToggleBoolean(false);
  const isDeadlineValid = useIsRsvpDeadlineValid();
  const isDisabled = !isDeadlineValid;

  const openModal = useCallback(() => {
    if (isDisabled) return;
    openModalRaw();
  }, [isDisabled, openModalRaw]);

  const value = useMemo(
    () => ({
      isModalOpen,
      openModal,
      closeModal,
      isDisabled,
    }),
    [isModalOpen, openModal, closeModal, isDisabled],
  );

  return (
    <MessageGuestsModalContext.Provider value={value}>
      {children}
    </MessageGuestsModalContext.Provider>
  );
};

const GuestsMessagesContainer = () => {
  const { isModalOpen, closeModal } = useMessageGuestsModal();

  return (
    <DirtyMessageFlowProvider>
      <CreateMessageContainer isOpen={isModalOpen} onClose={closeModal} />
    </DirtyMessageFlowProvider>
  );
};

export const GuestsMessagesProvider = ({ children }: { children: React.ReactNode }) => (
  <MessageGuestsModalProvider>
    {children}
    <GuestsMessagesContainer />
  </MessageGuestsModalProvider>
);
